import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import DataGrid, { Column, Editing, Popup as GridPopup, Form as GridForm } from 'devextreme-react/data-grid';
import Popup from 'devextreme-react/popup';
import Form, { Item as FormItem, GroupItem, SimpleItem, ButtonItem } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import {
  getPortalUsers,
  createPortalUser,
  updatePortalUser,
  deletePortalUser,
  resetPortalUserPassword,
  type PortalUser,
} from '../../api/portalUsers';

import {
  CardActivities,
  CardNotes,
  CardTasks,
  WorkTaskTable,
} from '../../components';

import { Relation } from '../../types/crm-contact';
import { Notes } from '../../types/card-notes';
import {
  getRelationByKey,
  getNotes,
  createWorkLocation,
  updateWorkLocation,
  deleteWorkLocation,
} from '../../api/relations';
import { useAuth } from '../../contexts/auth';
import './crm-contact-details.scss';

export interface State {
  relationOid: string;
  backTo: string;
  workTaskOid?: string;
}

export const CRMContactDetails = () => {
  const [data, setData] = useState<Relation>();
  const [notes, setNotes] = useState<Notes>();
  const [notesCount, setNotesCount] = useState(0);
  const [remarks, setRemarks] = useState<Notes>();
  const [remarksCount, setRemarksCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  // Portal user management state
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([]);
  const [portalUserPopupVisible, setPortalUserPopupVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [portalUserFormData, setPortalUserFormData] = useState<any>({});
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetPasswordOid, setResetPasswordOid] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const state = useLocation().state as State;
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadData();
    loadPortalUsers();
  }, []);

  const loadPortalUsers = useCallback(() => {
    getPortalUsers(state.relationOid, user?.token ?? '')
      .then(setPortalUsers)
      .catch(() => setPortalUsers([]));
  }, []);

  const openCreatePortalUser = () => {
    setIsEditMode(false);
    setPortalUserFormData({ FirstName: '', LastName: '', Email: '', Password: '', IsActive: true, PortalRole: 'Admin', AuthorizedLocationOids: [] });
    setPortalUserPopupVisible(true);
  };

  const openEditPortalUser = useCallback((rowData: PortalUser) => {
    setIsEditMode(true);
    setPortalUserFormData({ Oid: rowData.Oid, FirstName: rowData.FirstName, LastName: rowData.LastName, IsActive: rowData.IsActive, PortalRole: rowData.PortalRole ?? 'Admin', AuthorizedLocationOids: rowData.AuthorizedLocationOids ?? [] });
    setPortalUserPopupVisible(true);
  }, []);

  const openResetPassword = useCallback((rowData: PortalUser) => {
    setResetPasswordOid(rowData.Oid);
    setNewPassword('');
    setResetPasswordVisible(true);
  }, []);

  const handleDeletePortalUser = useCallback(async(rowData: PortalUser) => {
    const ok = await confirm('Portaalgebruiker verwijderen?', 'Bevestigen');
    if (!ok) return;
    try {
      await deletePortalUser(rowData.Oid, user?.token ?? '');
      notify('Gebruiker verwijderd', 'success', 2000);
      loadPortalUsers();
    } catch {
      notify('Fout bij verwijderen', 'error', 2000);
    }
  }, []);

  const handleSavePortalUser = useCallback(async() => {
    try {
      if (isEditMode) {
        await updatePortalUser(
          { Oid: portalUserFormData.Oid, FirstName: portalUserFormData.FirstName, LastName: portalUserFormData.LastName, IsActive: portalUserFormData.IsActive, PortalRole: portalUserFormData.PortalRole ?? 'Admin', AuthorizedLocationOids: portalUserFormData.AuthorizedLocationOids ?? [] },
          user?.token ?? ''
        );
        notify('Gebruiker bijgewerkt', 'success', 2000);
      } else {
        await createPortalUser(
          { FirstName: portalUserFormData.FirstName, LastName: portalUserFormData.LastName, Email: portalUserFormData.Email, Password: portalUserFormData.Password, RelationOid: state.relationOid, PortalRole: portalUserFormData.PortalRole ?? 'Admin', AuthorizedLocationOids: portalUserFormData.AuthorizedLocationOids ?? [] },
          user?.token ?? ''
        );
        notify('Gebruiker aangemaakt', 'success', 2000);
      }
      setPortalUserPopupVisible(false);
      loadPortalUsers();
    } catch (err: any) {
      notify(err?.response?.data || 'Fout bij opslaan', 'error', 3000);
    }
  }, [isEditMode, portalUserFormData]);

  const handleResetPassword = useCallback(async() => {
    if (!newPassword || newPassword.length < 8) {
      notify('Wachtwoord moet minimaal 8 tekens zijn', 'error', 2000);
      return;
    }
    try {
      await resetPortalUserPassword(resetPasswordOid, newPassword, user?.token ?? '');
      notify('Wachtwoord gewijzigd', 'success', 2000);
      setResetPasswordVisible(false);
    } catch {
      notify('Fout bij wachtwoord resetten', 'error', 2000);
    }
  }, [resetPasswordOid, newPassword]);

  function isNullOrEmpty(str: string | undefined | null) {
    return !str || str.trim() === '';
  }

  const loadData = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      getRelationByKey(state.relationOid, user?.token).then((d) => setData(d)),
      getNotes(state.relationOid, user?.token).then((d) => {
        const mapped: Notes = d.map((item) => ({
          text: item.Remark,
          date: item.MaintenanceDate,
          manager: item.DoneByName,
          quotationSent: item.QuotationSent,
          remarkDone: item.RemarkDone,
          moreWorkNeeded: item.MoreWorkNeeded,
        }));
        setNotes(mapped.filter((o) => o.moreWorkNeeded));
        setNotesCount(d.filter((o) => !o.QuotationSent && o.MoreWorkNeeded).length);
        setRemarks(mapped.filter((o) => !o.moreWorkNeeded && !isNullOrEmpty(o.text)));
        setRemarksCount(d.filter((o) => !o.MoreWorkNeeded && !o.RemarkDone && !isNullOrEmpty(o.Remark)).length);
      }),
    ])
      .finally(() => setIsLoading(false));
  }, []);

  const refresh = useCallback(() => {
    loadData();
  }, []);

  const goBackClick = useCallback(() => {
    if (state.backTo === 'workTaskDetails' && state.workTaskOid) {
      navigate('/work-task-details', { state: { workTaskOid: state.workTaskOid, relationOid: state.relationOid, backTo: 'crmDetails' } });
    } else if (state.backTo === 'taskList') {
      navigate('/maintenance');
    } else if (state.backTo === 'dashboard') {
      navigate('/analytics-dashboard');
    } else {
      navigate('/crm-contact-list');
    }
  }, []);

  const fullAddress =
    data && (data.Address || data.City)
      ? [
          [data.Address, data.AddressNr].filter(Boolean).join(' '),
          [data.ZipCode, data.City].filter(Boolean).join(' '),
        ]
          .filter(Boolean)
          .join(', ')
      : '-';

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-contact-details'>
        <Toolbar className='toolbar-details'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={goBackClick} />
          </ToolbarItem>
          <ToolbarItem location='before'>
            <span className='toolbar-title'>{data?.Name ?? 'Laden...'}</span>
          </ToolbarItem>
          <ToolbarItem location='after' locateInMenu='auto'>
            <Button text='Vernieuwen' icon='refresh' stylingMode='text' onClick={refresh} />
          </ToolbarItem>
        </Toolbar>

        {isLoading ? (
          <div className='loading-placeholder'>Laden...</div>
        ) : data ? (
          <>
            {/* ── Summary banner ── */}
            <div className='summary-banner'>
              <div className='banner-item'>
                <i className='dx-icon dx-icon-home banner-icon' />
                <span className='banner-name'>{data.Name}</span>
              </div>
              {data.City && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-map banner-icon' />
                  <span>{data.City}</span>
                </div>
              )}
              {data.TelephoneNumber && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-tel banner-icon' />
                  <span>{data.TelephoneNumber}</span>
                </div>
              )}
              {data.Email && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-email banner-icon' />
                  <span>{data.Email}</span>
                </div>
              )}
              <button
                className='banner-expand-btn'
                onClick={() => setDetailsExpanded(!detailsExpanded)}
              >
                <i className={`dx-icon dx-icon-${detailsExpanded ? 'chevronup' : 'chevrondown'}`} />
                <span>{detailsExpanded ? 'Verbergen' : 'Details'}</span>
              </button>
            </div>

            {/* ── Stacked cards ── */}
            <div className='panels'>
              {/* Relatie gegevens */}
              {detailsExpanded && (
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-card card-header-icon' />
                  <span>Relatie gegevens</span>
                </div>
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='info-label'>Relatie Nr</span>
                    <span className='info-value accent'>{data.CustomerNumber || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Bedrijf</span>
                    <span className='info-value'>{data.Name || '-'}</span>
                  </div>
                  {data.Company && (
                    <div className='info-row'>
                      <span className='info-label'>Bedrijfsnaam</span>
                      <span className='info-value'>{data.Company}</span>
                    </div>
                  )}
                  <div className='info-row'>
                    <span className='info-label'>Contactpersoon</span>
                    <span className='info-value'>{data.ContactPerson || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Adres</span>
                    <span className='info-value'>{fullAddress}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Telefoon</span>
                    <span className='info-value'>{data.TelephoneNumber || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Email</span>
                    <span className='info-value'>{data.Email || '-'}</span>
                  </div>
                  {data.InvoiceEmail && (
                    <div className='info-row'>
                      <span className='info-label'>Factuur email</span>
                      <span className='info-value'>{data.InvoiceEmail}</span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Werklocaties */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-map card-header-icon' />
                  <span>Werklocaties</span>
                  {(data.WorkLocations?.length ?? 0) > 0 && (
                    <span className='badge'>{data.WorkLocations!.length}</span>
                  )}
                </div>
                <div className='card-body'>
                  <DataGrid
                    dataSource={data.WorkLocations ?? []}
                    keyExpr='Oid'
                    showBorders={false}
                    noDataText='Geen werklocaties'
                    onSaving={async(e) => {
                      const insertChange = e.changes?.find(c => c.type === 'insert');
                      const updateChange = e.changes?.find(c => c.type === 'update');
                      const removeChange = e.changes?.find(c => c.type === 'remove');
                      if (insertChange) {
                        e.cancel = true;
                        try {
                          await createWorkLocation(
                            { RelationOid: state.relationOid, Name: insertChange.data?.Name ?? '', ...insertChange.data },
                            user?.token ?? ''
                          );
                          notify('Werklocatie toegevoegd', 'success', 2000);
                          loadData();
                        } catch {
                          notify('Fout bij toevoegen werklocatie', 'error', 2000);
                        }
                      } else if (updateChange) {
                        e.cancel = true;
                        try {
                          const oid = updateChange.key?.toString?.() ?? updateChange.key;
                          await updateWorkLocation(
                            oid,
                            { RelationOid: state.relationOid, Name: updateChange.data?.Name ?? '', ...updateChange.data },
                            user?.token ?? ''
                          );
                          notify('Werklocatie bijgewerkt', 'success', 2000);
                          loadData();
                        } catch {
                          notify('Fout bij bijwerken werklocatie', 'error', 2000);
                        }
                      } else if (removeChange) {
                        e.cancel = true;
                        try {
                          const oid = removeChange.key?.toString?.() ?? removeChange.key;
                          await deleteWorkLocation(oid, user?.token ?? '');
                          notify('Werklocatie verwijderd', 'success', 2000);
                          loadData();
                        } catch {
                          notify('Fout bij verwijderen werklocatie', 'error', 2000);
                        }
                      }
                    }}>
                    <Editing
                      useIcons
                      allowAdding
                      allowUpdating
                      allowDeleting
                      mode='popup'>
                      <GridPopup title='Werklocatie' showTitle width={560} height='auto' />
                      <GridForm>
                        <FormItem dataField='Name' colSpan={2} isRequired />
                        <FormItem dataField='Address' />
                        <FormItem dataField='AddressNr' />
                        <FormItem dataField='ZipCode' />
                        <FormItem dataField='City' />
                        <FormItem dataField='ContactPerson' />
                        <FormItem dataField='TelephoneNumber' />
                      </GridForm>
                    </Editing>
                    <Column dataField='Name' caption='Naam' />
                    <Column dataField='Address' caption='Straat' />
                    <Column dataField='AddressNr' caption='Nr' width={70} />
                    <Column dataField='ZipCode' caption='Postcode' width={100} />
                    <Column dataField='City' caption='Stad' />
                    <Column dataField='ContactPerson' caption='Contactpersoon' />
                    <Column dataField='TelephoneNumber' caption='Telefoon' />
                    <Column type='buttons' width={80} />
                  </DataGrid>
                </div>
              </div>

              {/* Opdrachten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-toolbox card-header-icon' />
                  <span>Opdrachten</span>
                </div>
                <div className='card-body'>
                  <WorkTaskTable isLoading={false} relationOid={state.relationOid} backTo='crmDetails' />
                </div>
              </div>

              {/* Contracten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-docfile card-header-icon' />
                  <span>Contracten</span>
                </div>
                <div className='card-body'>
                  <CardTasks isLoading={isLoading} relationOid={state.relationOid} />
                </div>
              </div>

              {/* Documenten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-folder card-header-icon' />
                  <span>Documenten</span>
                </div>
                <div className='card-body'>
                  <CardActivities relationOid={state.relationOid} />
                </div>
              </div>

              {/* Offerte aanvraag */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-warning card-header-icon' />
                  <span>Offerte aanvraag</span>
                  {notesCount > 0 && <span className='badge'>{notesCount}</span>}
                </div>
                <div className='card-body'>
                  <CardNotes items={notes} />
                </div>
              </div>

              {/* Opmerkingen */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-comment card-header-icon' />
                  <span>Opmerkingen</span>
                  {remarksCount > 0 && <span className='badge'>{remarksCount}</span>}
                </div>
                <div className='card-body'>
                  <CardNotes items={remarks} />
                </div>
              </div>

              {/* Portaalgebruikers */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-group card-header-icon' />
                  <span>Portaalgebruikers</span>
                  {portalUsers.length > 0 && <span className='badge'>{portalUsers.length}</span>}
                </div>
                <div className='card-body'>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button text='Toevoegen' icon='plus' stylingMode='outlined' type='default' onClick={openCreatePortalUser} />
                  </div>
                  <DataGrid
                    dataSource={portalUsers}
                    keyExpr='Oid'
                    showBorders={false}
                    noDataText='Geen portaalgebruikers'
                  >
                    <Column dataField='FirstName' caption='Voornaam' />
                    <Column dataField='LastName' caption='Achternaam' />
                    <Column dataField='UserName' caption='E-mail' />
                    <Column dataField='PortalRole' caption='Rol' width={100} />
                    <Column dataField='IsActive' caption='Actief' dataType='boolean' width={80} />
                    <Column
                      caption='Acties'
                      width={120}
                      cellRender={({ data }: { data: PortalUser }) => (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button icon='key' hint='Wachtwoord resetten' stylingMode='text' onClick={() => openResetPassword(data)} />
                          <Button icon='edit' hint='Bewerken' stylingMode='text' onClick={() => openEditPortalUser(data)} />
                          <Button icon='trash' hint='Verwijderen' stylingMode='text' onClick={() => handleDeletePortalUser(data)} />
                        </div>
                      )}
                    />
                  </DataGrid>
                </div>
              </div>
            </div>

            {/* Portal User Create/Edit Popup */}
            <Popup
              visible={portalUserPopupVisible}
              title={isEditMode ? 'Portaalgebruiker bewerken' : 'Portaalgebruiker toevoegen'}
              width={480}
              height='auto'
              onHiding={() => setPortalUserPopupVisible(false)}
              dragEnabled={false}
              hideOnOutsideClick
            >
              <Form
                formData={portalUserFormData}
                onFieldDataChanged={(e) =>
                  setPortalUserFormData((prev: any) => ({ ...prev, [e.dataField!]: e.value }))
                }
              >
                <GroupItem colCount={2}>
                  <SimpleItem dataField='FirstName' label={{ text: 'Voornaam' }} isRequired />
                  <SimpleItem dataField='LastName' label={{ text: 'Achternaam' }} isRequired />
                </GroupItem>
                {!isEditMode && (
                  <SimpleItem dataField='Email' label={{ text: 'E-mailadres' }} isRequired />
                )}
                {!isEditMode && (
                  <SimpleItem
                    dataField='Password'
                    label={{ text: 'Wachtwoord' }}
                    editorType='dxTextBox'
                    editorOptions={{ mode: 'password', placeholder: 'Min. 8 tekens' }}
                    isRequired
                  />
                )}
                <SimpleItem dataField='IsActive' label={{ text: 'Actief' }} editorType='dxCheckBox' />
                <SimpleItem
                  dataField='PortalRole'
                  label={{ text: 'Rol' }}
                  editorType='dxSelectBox'
                  editorOptions={{
                    items: ['Admin', 'Manager'],
                    value: portalUserFormData.PortalRole ?? 'Admin',
                  }}
                />
                {portalUserFormData.PortalRole === 'Manager' && (
                  <SimpleItem
                    dataField='AuthorizedLocationOids'
                    label={{ text: 'Toegewezen locaties' }}
                    editorType='dxTagBox'
                    editorOptions={{
                      items: (data?.WorkLocations ?? []).map((wl: any) => ({ Oid: wl.Oid, Name: wl.Name })),
                      displayExpr: 'Name',
                      valueExpr: 'Oid',
                      value: portalUserFormData.AuthorizedLocationOids ?? [],
                      showSelectionControls: true,
                      searchEnabled: true,
                    }}
                  />
                )}
                <ButtonItem
                  buttonOptions={{
                    text: 'Opslaan',
                    type: 'default',
                    useSubmitBehavior: false,
                    onClick: handleSavePortalUser,
                  }}
                />
              </Form>
            </Popup>

            {/* Reset Password Popup */}
            <Popup
              visible={resetPasswordVisible}
              title='Wachtwoord resetten'
              width={360}
              height='auto'
              onHiding={() => setResetPasswordVisible(false)}
              dragEnabled={false}
              hideOnOutsideClick
            >
              <Form>
                <SimpleItem
                  dataField='newPassword'
                  label={{ text: 'Nieuw wachtwoord' }}
                  editorType='dxTextBox'
                  editorOptions={{ mode: 'password', value: newPassword, placeholder: 'Min. 8 tekens', onValueChanged: (e: any) => setNewPassword(e.value) }}
                />
                <ButtonItem
                  buttonOptions={{
                    text: 'Opslaan',
                    type: 'default',
                    useSubmitBehavior: false,
                    onClick: handleResetPassword,
                  }}
                />
              </Form>
            </Popup>
          </>
        ) : (
          <div className='loading-placeholder'>Relatie niet gevonden</div>
        )}
      </div>
    </ScrollView>
  );
};
